import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://mrm.lemda.lk/api';

// Inline SVG placeholder for broken/missing images
const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1000" height="600" viewBox="0 0 1000 600">
    <defs>
      <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stop-color="#e5e7eb"/>
        <stop offset="100%" stop-color="#cbd5e1"/>
      </linearGradient>
    </defs>
    <rect width="1000" height="600" fill="url(#g)"/>
    <g fill="#475569" font-family="Arial, Helvetica, sans-serif" text-anchor="middle">
      <text x="500" y="290" font-size="28" font-weight="700">Latest News</text>
      <text x="500" y="325" font-size="14" opacity="0.85">Image not available</text>
    </g>
  </svg>`);

const css = `
.news-section { background:#F1F5F9; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; padding: clamp(2.5rem, 6vw, 6rem) 1rem; }
.news-container { max-width: 1200px; margin: 0 auto; }
.news-title { color:#1e3a8a; font-size: clamp(1.6rem, 3vw, 2rem); font-weight: 800; margin: 0 0 1.5rem; }

/* Featured */
.featured-article { background:#fff; display:grid; grid-template-columns: 1fr; border-radius: 16px; overflow:hidden; border:1px solid #e5e7eb; }
@media (min-width: 1024px){ .featured-article { grid-template-columns: 1fr 1fr; } }
.featured-image { position: relative; min-height: 260px; background:#e5e7eb; }
.featured-image img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
.featured-content { padding: clamp(1.25rem, 3vw, 2rem); }
.article-title { margin:0; color:#0f172a; font-size: clamp(1.25rem, 2.6vw, 1.8rem); font-weight: 800; line-height:1.2; }
.article-excerpt { margin-top: .75rem; color:#475569; line-height:1.6; }
.read-more { display:inline-block; margin-top:1rem; position:relative; color:#1e3a8a; text-decoration:none; font-weight:800; }
.read-more::before { content:""; position:absolute; left:-28px; top:50%; transform:translateY(-50%); width:24px; height:3px; background:#1e3a8a; opacity:.7; }
.read-more:hover { color:#173074; }

/* Grid of others */
.other-articles { display:grid; grid-template-columns:1fr; gap: 1.25rem; margin-top: clamp(1.5rem, 3vw, 2rem); }
@media (min-width: 768px){ .other-articles { grid-template-columns: 1fr 1fr; } }
.item { background:#fff; border:1px solid #e5e7eb; border-radius: 12px; padding: 1rem 1.1rem; display:grid; grid-template-columns: 88px 1fr; gap:.9rem; align-items:center; }
.item-thumb { width:88px; height:88px; border-radius: 10px; overflow:hidden; background:#e5e7eb; }
.item-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
.item-title { margin:0; color:#0f172a; font-weight:700; font-size: 1rem; }
.item-meta { color:#64748b; font-size:.85rem; margin-top:.15rem; }
.item-read { display:inline-block; margin-top:.35rem; color:#1e3a8a; text-decoration:none; font-weight:700; }
.item-read:hover { color:#173074; }

/* Skeletons */
@keyframes shimmer { 0%{background-position:-450px 0} 100%{background-position:450px 0} }
.skel-featured { border-radius:16px; overflow:hidden; border:1px solid #e5e7eb; background:#fff; }
.skel-rect { height:260px; background:linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%); background-size:900px 100%; animation:shimmer 1.2s infinite; }
.skel-lines { padding:1rem; display:grid; gap:.6rem; }
.skel-line { height:14px; width:60%; background:linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%); background-size:900px 100%; animation:shimmer 1.2s infinite; border-radius:6px; }
.skel-line.sm { width:40%; height:12px; }
.skel-item { background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding:1rem; display:grid; grid-template-columns:88px 1fr; gap:.9rem; }
.skel-thumb { width:88px; height:88px; background:linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%); background-size:900px 100%; animation:shimmer 1.2s infinite; border-radius:10px; }
.skel-txt { display:grid; gap:.5rem; align-content:center; }
`;

// Helper: format date like "Aug 18, 2025"
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function LatestNewsSection() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/news.php?status=published&limit=5`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.ok) throw new Error(json.error || 'Failed to load');
        if (!ignore) setArticles(Array.isArray(json.data) ? json.data : []);
      } catch (e) {
        if (!ignore) setErr(e.message || 'Network error');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  if (!loading && articles.length === 0) return null;
  const [featured, ...others] = articles;

  return (
    <>
      <style>{css}</style>
      <section className="news-section" aria-label="Latest news and insights">
        <div className="news-container">
          <h2 className="news-title">Latest news &amp; insights</h2>

          {/* Error state */}
          {err && <div style={{ color: '#b91c1c', marginBottom: '1rem', fontWeight: 700 }}>{err}</div>}

          {/* Loading skeleton */}
          {loading && (
            <>
              <div className="skel-featured">
                <div className="skel-rect" />
                <div className="skel-lines">
                  <div className="skel-line" />
                  <div className="skel-line sm" />
                </div>
              </div>
              <div className="other-articles" style={{ marginTop: '1.25rem' }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="skel-item">
                    <div className="skel-thumb" />
                    <div className="skel-txt">
                      <div className="skel-line" style={{ width: '80%', height: 12 }} />
                      <div className="skel-line" style={{ width: '40%', height: 10 }} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Featured article */}
          {!loading && featured && (
            <article className="featured-article">
              <div className="featured-image">
                <img
                  src={featured.full_image_url || featured.cover_image_url || PLACEHOLDER}
                  alt={featured.title}
                  onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
                  loading="lazy"
                />
              </div>
              <div className="featured-content">
                <h3 className="article-title">{featured.title}</h3>
                {featured.excerpt && <p className="article-excerpt">{featured.excerpt}</p>}
                <a
                  href={featured.href || `/news/${featured.slug}`}
                  className="read-more"
                  aria-label={`Read more: ${featured.title}`}
                >
                  Read more
                </a>
                {featured.published_at && (
                  <div style={{ marginTop: '0.5rem', color: '#64748b', fontSize: '.9rem' }}>
                    {formatDate(featured.published_at)}
                  </div>
                )}
              </div>
            </article>
          )}

          {/* Other articles */}
          {!loading && others.length > 0 && (
            <div className="other-articles">
              {others.map((art) => (
                <article key={art.id} className="item">
                  <div className="item-thumb">
                    <img
                      src={art.full_image_url || art.cover_image_url || PLACEHOLDER}
                      alt={art.title}
                      onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h4 className="item-title">{art.title}</h4>
                    {art.published_at && (
                      <div className="item-meta">{formatDate(art.published_at)}</div>
                    )}
                    <a
                      href={art.href || `/news/${art.slug}`}
                      className="item-read"
                      aria-label={`Read ${art.title}`}
                    >
                      Read
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
